/**
 * ManagePartnersModal
 *
 * Modal for managing substrate partners (add, edit, deactivate/reactivate).
 */

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Loader2, Plus, UserX, UserCheck } from 'lucide-react'

const ARRANGEMENT_LABELS = { cost_share: 'Cost Share', sale: 'Sale' }
const CATEGORY_LABELS = { container_rental: 'Container Rental', electricity: 'Electricity', transport: 'Transport', other: 'Other' }

const EMPTY_FORM = {
  name: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  arrangement_type: 'cost_share',
  notes: ''
}

export default function ManagePartnersModal({ open, onOpenChange, hook }) {
  const { partners, costShares, addPartner, deactivatePartner, reactivatePartner, addCostShare, deleteCostShare, OVERHEAD_CATEGORIES } = hook
  const [newPartner, setNewPartner] = useState({ ...EMPTY_FORM })
  const [saving, setSaving] = useState(false)
  const [toggling, setToggling] = useState(null)
  const [error, setError] = useState(null)
  const [newShare, setNewShare] = useState({ partner_id: '', overhead_category: 'container_rental', share_ratio: '50', effective_from: '' })
  const [savingShare, setSavingShare] = useState(false)
  const [deletingShare, setDeletingShare] = useState(null)

  const handleChange = (field, value) => {
    setNewPartner(prev => ({ ...prev, [field]: value }))
  }

  const handleAdd = async (e) => {
    e.preventDefault()
    if (!newPartner.name.trim()) return
    setSaving(true)
    setError(null)

    try {
      await addPartner({
        ...newPartner,
        name: newPartner.name.trim(),
        contact_name: newPartner.contact_name.trim() || null,
        contact_email: newPartner.contact_email.trim() || null,
        contact_phone: newPartner.contact_phone.trim() || null,
        notes: newPartner.notes.trim() || null
      })
      setNewPartner({ ...EMPTY_FORM })
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (partner) => {
    setToggling(partner.id)
    try {
      if (partner.is_active) {
        await deactivatePartner(partner.id)
      } else {
        await reactivatePartner(partner.id)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setToggling(null)
    }
  }

  const handleAddShare = async (e) => {
    e.preventDefault()
    if (!newShare.partner_id || !newShare.effective_from) return
    setSavingShare(true)
    setError(null)
    try {
      await addCostShare({
        partner_id: newShare.partner_id,
        overhead_category: newShare.overhead_category,
        share_ratio: parseFloat(newShare.share_ratio) / 100,
        effective_from: newShare.effective_from,
      })
      setNewShare(prev => ({ ...prev, overhead_category: 'container_rental', share_ratio: '50', effective_from: '' }))
    } catch (err) {
      setError(err.message)
    } finally {
      setSavingShare(false)
    }
  }

  const handleDeleteShare = async (id) => {
    setDeletingShare(id)
    try {
      await deleteCostShare(id)
    } catch (err) {
      setError(err.message)
    } finally {
      setDeletingShare(null)
    }
  }

  const activePartners = partners.filter(p => p.is_active)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Partners</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add Partner Form */}
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="partner_name" className="text-xs">Name *</Label>
                <Input
                  id="partner_name"
                  value={newPartner.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Partner name"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="contact_name" className="text-xs">Contact Name</Label>
                <Input
                  id="contact_name"
                  value={newPartner.contact_name}
                  onChange={(e) => handleChange('contact_name', e.target.value)}
                  placeholder="Contact person"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="contact_email" className="text-xs">Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={newPartner.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="contact_phone" className="text-xs">Phone</Label>
                <Input
                  id="contact_phone"
                  value={newPartner.contact_phone}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  placeholder="Phone number"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="arrangement_type" className="text-xs">Arrangement</Label>
                <Select
                  value={newPartner.arrangement_type}
                  onValueChange={(v) => handleChange('arrangement_type', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cost_share">Cost Share</SelectItem>
                    <SelectItem value="sale">Sale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1">
                <Label htmlFor="partner_notes" className="text-xs">Notes</Label>
                <Input
                  id="partner_notes"
                  value={newPartner.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Optional notes..."
                />
              </div>
              <Button type="submit" size="sm" disabled={saving || !newPartner.name.trim()}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Add
              </Button>
            </div>
          </form>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {/* Partners List */}
          {partners.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="text-left py-2 font-medium">Name</th>
                    <th className="text-left py-2 font-medium">Contact</th>
                    <th className="text-center py-2 font-medium">Arrangement</th>
                    <th className="text-center py-2 font-medium">Xero</th>
                    <th className="text-center py-2 font-medium">Status</th>
                    <th className="text-right py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map(p => (
                    <tr key={p.id} className={`border-b last:border-0 ${!p.is_active ? 'opacity-50' : ''}`}>
                      <td className="py-2 font-medium">{p.name}</td>
                      <td className="py-2 text-muted-foreground">{p.contact_name || '—'}</td>
                      <td className="text-center py-2">
                        <Badge variant="outline" className="text-xs">
                          {ARRANGEMENT_LABELS[p.arrangement_type] || p.arrangement_type}
                        </Badge>
                      </td>
                      <td className="text-center py-2">
                        {p.xero_contact_id ? (
                          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs">
                            Linked
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="text-center py-2">
                        <Badge className={p.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                        }>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="text-right py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(p)}
                          disabled={toggling === p.id}
                        >
                          {toggling === p.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : p.is_active ? (
                            <UserX className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <UserCheck className="h-4 w-4 text-green-600" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No partners yet</p>
          )}

          {/* Cost Shares Section */}
          {activePartners.length > 0 && (
            <>
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium mb-3">Cost Shares</h3>
                <form onSubmit={handleAddShare} className="flex items-end gap-2 mb-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Partner</Label>
                    <Select value={newShare.partner_id} onValueChange={(v) => setNewShare(prev => ({ ...prev, partner_id: v }))}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {activePartners.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Category</Label>
                    <Select value={newShare.overhead_category} onValueChange={(v) => setNewShare(prev => ({ ...prev, overhead_category: v }))}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OVERHEAD_CATEGORIES.map(c => (
                          <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Share %</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={newShare.share_ratio}
                      onChange={(e) => setNewShare(prev => ({ ...prev, share_ratio: e.target.value }))}
                      className="w-20"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">From</Label>
                    <Input
                      type="date"
                      value={newShare.effective_from}
                      onChange={(e) => setNewShare(prev => ({ ...prev, effective_from: e.target.value }))}
                      className="w-36"
                      required
                    />
                  </div>
                  <Button type="submit" size="sm" disabled={savingShare || !newShare.partner_id || !newShare.effective_from}>
                    {savingShare ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </form>

                {costShares.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-muted-foreground border-b">
                          <th className="text-left py-1 pr-4 font-medium">Partner</th>
                          <th className="text-left py-1 pr-4 font-medium">Category</th>
                          <th className="text-right py-1 pr-4 font-medium">Share</th>
                          <th className="text-left py-1 pr-4 font-medium">From</th>
                          <th className="text-right py-1 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {costShares.map(cs => (
                          <tr key={cs.id} className="border-b last:border-0">
                            <td className="py-1 pr-4">{cs.substrate_partners?.name || '—'}</td>
                            <td className="py-1 pr-4">{CATEGORY_LABELS[cs.overhead_category] || cs.overhead_category}</td>
                            <td className="text-right py-1 pr-4">{(Number(cs.share_ratio) * 100).toFixed(0)}%</td>
                            <td className="py-1 pr-4 text-muted-foreground">{cs.effective_from}</td>
                            <td className="text-right py-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteShare(cs.id)}
                                disabled={deletingShare === cs.id}
                              >
                                {deletingShare === cs.id
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <span className="text-xs text-muted-foreground hover:text-destructive">Remove</span>
                                }
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-2">No cost shares defined</p>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
